import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin, Warehouse, Truck, Clock } from "lucide-react";

// Eyebrow component
function Eyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

// Glass card component
function GlassCard({ children, className = "", ...rest }) {
  return (
    <motion.div
      className={`glass-card p-6 md:p-8 ${className}`}
      whileHover={{ y: -4, borderColor: 'rgba(86, 205, 236, 0.3)' }}
      transition={{ duration: 0.3 }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// Stagger reveal
function StaggerReveal({ children, index = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

const offices = [
  {
    city: "Dubai",
    country: "United Arab Emirates",
    entity: "Optrenex International General Trading LLC",
    role: "Head Office (HQ)",
    address: "Office 120 & 121, Al Salam Building, Al Buteen, Murshid Bazar, Al Sabkha, Deira, Dubai, UAE — PO Box 1540",
    features: ["Trade License: 1546242", "VAT TRN: 105290096400003", "Dubai Customs Code: AE-1209328", "Media License: 0559605"],
    x: "59%", y: "42%",
  },
  {
    city: "Mumbai",
    country: "India",
    entity: "Optrenex International Private Limited",
    role: "Registered Office & Global Sourcing",
    address: "201, Om Palace CHS Ltd, Dr. Ambedkar Road, Khar, Mumbai 400052",
    features: ["CIN: U47990MH2025PTC451227", "IEC: AAECO7780L", "UDYAM MSME: UDYAM-MH-18-0459803"],
    x: "67%", y: "48%",
  },
];

export default function GlobalPresence() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });
  const mapRef = useRef(null);
  const mapInView = useInView(mapRef, { once: true, amount: 0.2 });

  return (
    <main data-testid="global-presence-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 relative" ref={heroRef}>
        <div className="absolute inset-0 grid-bg opacity-20" />
        <motion.div
          className="max-w-7xl mx-auto px-fluid text-center relative z-10"
          initial={{ opacity: 0, y: 40 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Eyebrow>Worldwide Operations</Eyebrow>
          <h1 className="font-heading text-hero font-extrabold uppercase tracking-wider mt-4">
            Our Global <span className="text-cyan">Presence</span>
          </h1>
          <p className="mt-6 text-dim text-lg max-w-2xl mx-auto">
            Strategic locations in the UAE and India powering global gaming distribution with licensed import capabilities.
          </p>
        </motion.div>
      </section>

      {/* Interactive Map */}
      <section className="pb-24" ref={mapRef}>
        <div className="max-w-7xl mx-auto px-fluid">
          <motion.div
            className="relative w-full aspect-[2.2/1] rounded-card overflow-hidden border border-line bg-panel/50"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={mapInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="absolute inset-0 grid-bg opacity-30" />

            {/* SVG connections */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 50" preserveAspectRatio="none">
              <motion.line x1="59" y1="21" x2="61" y2="22" stroke="#56cdec" strokeWidth="0.2" strokeOpacity="0.4" strokeDasharray="0.8 0.8"
                initial={{ pathLength: 0 }} animate={mapInView ? { pathLength: 1 } : {}} transition={{ duration: 1.5, delay: 0.5 }} />
              <motion.line x1="60" y1="21.5" x2="67" y2="24" stroke="#56cdec" strokeWidth="0.15" strokeOpacity="0.3" strokeDasharray="0.8 0.8"
                initial={{ pathLength: 0 }} animate={mapInView ? { pathLength: 1 } : {}} transition={{ duration: 2, delay: 0.8 }} />
              <motion.line x1="61" y1="22" x2="67" y2="24" stroke="#7B61FF" strokeWidth="0.1" strokeOpacity="0.2" strokeDasharray="0.5 0.5"
                initial={{ pathLength: 0 }} animate={mapInView ? { pathLength: 1 } : {}} transition={{ duration: 2, delay: 1.1 }} />
            </svg>

            {/* Location dots */}
            {offices.map((office, i) => (
              <motion.div
                key={office.city}
                className="absolute flex flex-col items-center group cursor-pointer"
                style={{ left: office.x, top: office.y, transform: "translate(-50%, -50%)" }}
                initial={{ opacity: 0, scale: 0 }}
                animate={mapInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.3 + i * 0.15, type: "spring", stiffness: 200 }}
              >
                <div className="absolute w-5 h-5 rounded-full bg-cyan/20 animate-pulse" />
                <div className="w-3.5 h-3.5 rounded-full bg-cyan shadow-[0_0_16px_rgba(86,205,236,0.7)] relative z-10 group-hover:scale-150 transition-transform" />
                {/* Tooltip */}
                <div className="absolute bottom-full mb-3 opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100 z-20">
                  <div className="bg-panel/95 backdrop-blur-xl px-4 py-3 rounded-card border border-cyan/20 whitespace-nowrap shadow-[0_4px_24px_rgba(86,205,236,0.1)]">
                    <p className="font-ui text-sm font-semibold text-ink">{office.city}</p>
                    <p className="font-mono text-xs text-cyan">{office.role}</p>
                  </div>
                </div>
                <span className="hidden md:block mt-2 font-mono text-[10px] text-dim uppercase tracking-wider font-medium">
                  {office.city}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Location Cards */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-fluid">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offices.map((office, i) => (
              <StaggerReveal key={office.city} index={i}>
                <GlassCard className="h-full" data-testid={`location-card-${office.city.toLowerCase()}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-cyan-soft flex items-center justify-center">
                      <MapPin size={20} className="text-cyan" />
                    </div>
                    <div>
                      <h3 className="font-ui text-lg font-semibold text-ink">{office.city}</h3>
                      <p className="text-dim text-xs">{office.country}</p>
                    </div>
                  </div>
                  <p className="text-cyan text-sm font-ui font-medium mb-2">{office.role}</p>
                  <p className="text-dim text-xs mb-1">{office.entity}</p>
                  <p className="text-dim/60 text-xs mb-4">{office.address}</p>
                  <ul className="space-y-2">
                    {office.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2 text-dim text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-cyan" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </GlassCard>
              </StaggerReveal>
            ))}
          </div>

          {/* Logistics stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Warehouse, label: "Facilities", value: "2" },
              { icon: Truck, label: "Entities", value: "2" },
              { icon: MapPin, label: "Countries", value: "UAE + India" },
              { icon: Clock, label: "Licensed Since", value: "2026" },
            ].map((s, i) => (
              <StaggerReveal key={i} index={i}>
                <GlassCard className="text-center" data-testid={`logistics-stat-${i}`}>
                  <s.icon size={24} className="text-cyan mx-auto mb-3" />
                  <p className="font-heading text-2xl font-bold text-ink">{s.value}</p>
                  <p className="font-mono text-[10px] text-dim uppercase tracking-[0.15em] mt-1">{s.label}</p>
                </GlassCard>
              </StaggerReveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
